import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, set } from '@totalsoft/react-state-lens';
import { TextField, Autocomplete } from '@totalsoft/rocket-ui';
import { valueOrDefault } from 'utils/functions';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { MetadataDetailTableCell, MetadataDetailTableRow } from '../Styles';
import { required, shape } from '@totalsoft/pure-validations';
import FlexBox from 'components/common/FlexBox';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_DMS_WRITE } from 'constants/rightsCodes';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useTranslation } from 'react-i18next';
import { dataTypes } from '../../constants';
import { metadataStore } from '../metadataStore';
import { setApolloFetchingInternalValues } from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import IconButton from 'components/common/buttons/IconButton';
import { emptyString } from 'utils/constants';

const validator = shape({ code: required, name: required, dataType: required });

const MetadataDetailItemComponent = ({ lens, onDelete, validation, dirtyInfo, pending }) => {
  const canWrite = usePermission(ADMIN_DMS_WRITE);
  const { t } = useTranslation();
  const confirm = useConfirmDialog();

  const isNew = lens.isNew |> get;

  // #region Validation

  const [localValidation, validate] = useDirtyFieldValidation(validator);

  useEffect(() => {
    let internalDirtyInfo = { code: false, name: false };
    if (dirtyInfo && typeof dirtyInfo !== 'boolean') internalDirtyInfo = { ...internalDirtyInfo, ...dirtyInfo };
    validate(lens |> get, internalDirtyInfo);
  }, [validate, lens, dirtyInfo]);

  const getValid = useCallback(
    prop => {
      if (!dirtyInfo || typeof dirtyInfo === 'boolean' || !dirtyInfo[prop]) {
        return isValid(validation[prop]);
      }
      return isValid(localValidation[prop]);
    },
    [dirtyInfo, validation, localValidation]
  );

  const getError = useCallback(
    prop => {
      if (!dirtyInfo || typeof dirtyInfo === 'boolean' || !dirtyInfo[prop]) {
        return getErrors(validation[prop]);
      }
      return getErrors(localValidation[prop]);
    },
    [dirtyInfo, validation, localValidation]
  );

  // #endregion

  const handleDelete = () => {
    if (isNew) {
      onDelete(lens.id |> get);
      return;
    }
    confirm(
      () => {
        const storeMetadata = metadataStore.get()?.allValues || [];
        const newMetadata = storeMetadata.filter(metadata => metadata.id !== (lens.id |> get));
        setApolloFetchingInternalValues.get()(newMetadata);
        onDelete(lens.id |> get);
      },
      {
        content: t('Metadata.AreYouSureDeleteMetadata')
      }
    );
  };

  return (
    <MetadataDetailTableRow hover={!isNew} isNew={isNew}>
      <MetadataDetailTableCell>
        <TextField
          disabled={!canWrite || pending}
          fullWidth
          value={lens.name |> get |> valueOrDefault(emptyString)}
          onChange={lens.name |> set}
          error={!getValid('name')}
          helperText={getError('name')}
          inputProps={{
            maxLength: 255
          }}
        />
      </MetadataDetailTableCell>
      <MetadataDetailTableCell>
        <TextField
          fullWidth
          value={lens.code |> get |> valueOrDefault(emptyString)}
          disabled={!canWrite || pending}
          onChange={lens.code |> set}
          error={!getValid('code')}
          helperText={getError('code')}
        />
      </MetadataDetailTableCell>
      <MetadataDetailTableCell>
        <Autocomplete
          disabled={!canWrite || pending}
          fullWidth
          required
          valueKey={'id'}
          labelKey={'name'}
          value={lens.dataType |> get |> valueOrDefault(emptyString)}
          onChange={set(lens.dataType)}
          options={dataTypes}
          error={!getValid('dataType')}
          helperText={getError('dataType')}
          simpleValue
        />
      </MetadataDetailTableCell>
      <MetadataDetailTableCell>
        <TextField
          fullWidth
          value={lens.entityType |> get |> valueOrDefault(emptyString)}
          disabled={!canWrite || pending}
          onChange={lens.entityType |> set}
        />
      </MetadataDetailTableCell>
      <MetadataDetailTableCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton disabled={!canWrite || pending} type={isNew ? 'cancel' : 'delete'} onClick={handleDelete} />
        </FlexBox>
      </MetadataDetailTableCell>
    </MetadataDetailTableRow>
  );
};

MetadataDetailItemComponent.propTypes = {
  lens: PropTypes.object,
  onDelete: PropTypes.func,
  validation: PropTypes.object,
  dirtyInfo: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  pending: PropTypes.bool
};

export default MetadataDetailItemComponent;
